import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  LinearProgress,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Description as FileIcon,
  TableChart as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Close as CloseIcon,
  Preview as PreviewIcon,
  Transform as TransformIcon,
  CheckCircle as SuccessIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import documentParserService from '../services/documentParserService';

const DocumentImportArea = ({ onImport, isLoading, onLoadingChange }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [parseProgress, setParseProgress] = useState(0);
  const [parseStatus, setParseStatus] = useState('');
  const [error, setError] = useState('');
  const [previewDialog, setPreviewDialog] = useState(false);
  const [parsedData, setParsedData] = useState(null);

  // Desteklenen dosya tipleri
  const acceptedFileTypes = {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/msword': ['.doc'],
    'application/pdf': ['.pdf']
  };

  // Dosya simgesi getir
  const getFileIcon = (fileName) => {
    const extension = fileName.toLowerCase().split('.').pop();
    switch (extension) {
      case 'xlsx':
      case 'xls':
        return <ExcelIcon sx={{ color: '#1e7e34', fontSize: 32 }} />;
      case 'docx':
      case 'doc':
        return <FileIcon sx={{ color: '#2b5797', fontSize: 32 }} />;
      case 'pdf':
        return <PdfIcon sx={{ color: '#dc3545', fontSize: 32 }} />;
      default:
        return <FileIcon sx={{ color: '#6c757d', fontSize: 32 }} />;
    }
  };

  // Dosya boyutunu formatla
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Dosya parse işlemi
  const parseDocument = async (file) => {
    try {
      setError('');
      setParseProgress(0);
      setParseStatus('Belge okunuyor...');
      onLoadingChange?.(true);

      // Progress simulation
      const progressInterval = setInterval(() => {
        setParseProgress(prev => {
          if (prev < 90) return prev + 15;
          return prev;
        });
      }, 300);

      // Dosyayı parse et
      const result = await documentParserService.parseDocument(file);
      
      clearInterval(progressInterval);
      setParseProgress(100);
      setParseStatus('Form alanları başarıyla tespit edildi!');
      
      setParsedData(result);
      
      setTimeout(() => {
        onLoadingChange?.(false);
        setPreviewDialog(true);
      }, 500);
      
    } catch (error) {
      console.error('Parse error:', error);
      setError(error.message || 'Dosya işlenirken bir hata oluştu');
      setParseProgress(0);
      setParseStatus('');
      onLoadingChange?.(false);
    }
  };

  // Dosya bırakma işlemi
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      parseDocument(file);
    }
  }, []);

  // Dropzone hook
  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024, // 50MB
  });

  // Dosya kaldır
  const removeFile = () => {
    setSelectedFile(null);
    setParseProgress(0);
    setParseStatus('');
    setError('');
    setParsedData(null);
  };

  // Import işlemini onaylama
  const handleImportConfirm = () => {
    if (parsedData && onImport) {
      onImport(parsedData);
      setPreviewDialog(false);
      removeFile();
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Paper
        {...getRootProps()}
        sx={{
          p: 3,
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          borderRadius: 2,
          backgroundColor: isDragActive ? 'primary.light' : 'grey.50',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'primary.light',
          }
        }}
      >
        <input {...getInputProps()} />
        
        <Box sx={{ textAlign: 'center' }}>
          <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {isDragActive 
              ? 'Dosyayı buraya bırakın...' 
              : '⚡ Hızlı Form İçe Aktarma'
            }
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Word, Excel veya PDF formunuzu yükleyin - anında alanları tespit edeceğiz
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip label=".xlsx" size="small" variant="outlined" />
            <Chip label=".xls" size="small" variant="outlined" />
            <Chip label=".docx" size="small" variant="outlined" />
            <Chip label=".doc" size="small" variant="outlined" />
            <Chip label=".pdf" size="small" variant="outlined" />
          </Box>
          
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Maksimum dosya boyutu: 50MB
          </Typography>
        </Box>
      </Paper>

      {/* Dosya Reddi Uyarısı */}
      {fileRejections.length > 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {fileRejections[0].errors[0].message}
        </Alert>
      )}

      {/* Hata Mesajı */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {/* Seçilen Dosya */}
      {selectedFile && (
        <Paper sx={{ mt: 2, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {getFileIcon(selectedFile.name)}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {selectedFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(selectedFile.size)}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={removeFile} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Parse Progress */}
          {(parseProgress > 0 || parseStatus) && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">{parseStatus}</Typography>
                <Typography variant="body2">{parseProgress}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={parseProgress} />
            </Box>
          )}
        </Paper>
      )}

      {/* Önizleme Diyaloğu */}
      <Dialog 
        open={previewDialog} 
        onClose={() => setPreviewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between' 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PreviewIcon color="primary" />
            <Typography variant="h6">Form Önizleme</Typography>
          </Box>
          <IconButton onClick={() => setPreviewDialog(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
          {parsedData && (
            <>
              {/* Form Başlığı */}
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom>
                  {parsedData.title}
                </Typography>
                <Chip 
                  icon={<SuccessIcon />}
                  label={`${parsedData.fields.length} alan tespit edildi`} 
                  color="success" 
                  size="small" 
                />
              </Paper>

              {/* Tespit Edilen Alanlar */}
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Tespit Edilen Form Alanları:
              </Typography>
              
              <List>
                {parsedData.fields.map((field, index) => (
                  <React.Fragment key={field.id}>
                    <ListItem>
                      <ListItemIcon>
                        <TransformIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1">{field.label}</Typography>
                            {field.required && (
                              <Chip label="Zorunlu" size="small" color="error" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Chip 
                              label={field.type} 
                              size="small" 
                              variant="outlined" 
                            />
                            {field.options && (
                              <Typography variant="caption" color="text.secondary">
                                {field.options.length} seçenek
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < parsedData.fields.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setPreviewDialog(false)}
            variant="outlined"
          >
            İptal
          </Button>
          <Button 
            onClick={handleImportConfirm}
            variant="contained"
            startIcon={<TransformIcon />}
          >
            Form Builder'a Aktar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentImportArea;
